export type TemplateListItem = {
  id: string
  name?: string
  frameType?: 'frame' | 'spineFrame'
  type?: string
  cover?: string
  coverThumb?: string
  preview?: string
  previewThumb?: string
  videoCoverPreview?: string
  videoPreviewPreview?: string
  character?: number
  spineZipUrl?: string
  reCreateCount?: number
  dailyReCreateCount?: number
}

export function resolveFrameListType(type: string | undefined): string {
  const trimmed = type?.trim()
  if (!trimmed) return '10'
  return trimmed
}

export function frameListIncludesSpine(type: string | undefined): boolean {
  const resolved = resolveFrameListType(type)
  return resolved.length >= 2 && resolved[0] === '1' && resolved[1] === '1'
}

export function isSpineTemplateItem(item: TemplateListItem): boolean {
  if (item.frameType === 'spineFrame') return true
  if (item.frameType === 'frame') return false
  return Boolean(
    item.videoCoverPreview ||
      item.videoPreviewPreview ||
      item.character != null ||
      item.spineZipUrl,
  )
}

export function getTemplateListItemKind(
  item: TemplateListItem,
): 'frame' | 'spineFrame' {
  return isSpineTemplateItem(item) ? 'spineFrame' : 'frame'
}

export function buildTemplateListAfterCursor(
  item: TemplateListItem,
  type: string | undefined,
): string {
  if (frameListIncludesSpine(type)) {
    return isSpineTemplateItem(item)
      ? `spineFrame-${item.id}`
      : `frame-${item.id}`
  }
  return item.id
}

export function parseTemplateListLimit(
  query: Record<string, string> | undefined,
): number {
  const raw = query?.limit
  const parsed = raw ? Number.parseInt(raw, 10) : 10
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 10
}

function normalizeTemplateListItem(
  raw: Record<string, unknown>,
): TemplateListItem {
  const rawFrameType = raw.frameType
  const frameType =
    rawFrameType === 'frame' || rawFrameType === 'spineFrame'
      ? rawFrameType
      : undefined
  const dailyCount = raw.dailyCount
  const dailyReCreateCount =
    dailyCount &&
    typeof dailyCount === 'object' &&
    typeof (dailyCount as { reCreateCount?: unknown }).reCreateCount ===
      'number'
      ? ((dailyCount as { reCreateCount: number }).reCreateCount as number)
      : undefined

  return {
    id: String(raw.id),
    frameType,
    name: typeof raw.name === 'string' ? raw.name : undefined,
    type: typeof raw.type === 'string' ? raw.type : undefined,
    cover: typeof raw.cover === 'string' ? raw.cover : undefined,
    coverThumb: typeof raw.coverThumb === 'string' ? raw.coverThumb : undefined,
    preview: typeof raw.preview === 'string' ? raw.preview : undefined,
    previewThumb:
      typeof raw.previewThumb === 'string' ? raw.previewThumb : undefined,
    videoCoverPreview:
      typeof raw.videoCoverPreview === 'string'
        ? raw.videoCoverPreview
        : undefined,
    videoPreviewPreview:
      typeof raw.videoPreviewPreview === 'string'
        ? raw.videoPreviewPreview
        : undefined,
    character: typeof raw.character === 'number' ? raw.character : undefined,
    spineZipUrl:
      typeof raw.spineZipUrl === 'string' ? raw.spineZipUrl : undefined,
    reCreateCount:
      typeof raw.reCreateCount === 'number' ? raw.reCreateCount : undefined,
    dailyReCreateCount,
  }
}

export function parseTemplateListResponse(data: unknown): TemplateListItem[] {
  if (!data) return []
  if (Array.isArray(data)) {
    return data
      .filter(
        (item): item is Record<string, unknown> =>
          item != null && typeof item === 'object' && typeof item.id === 'string',
      )
      .map(normalizeTemplateListItem)
  }
  if (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { data?: unknown }).data)
  ) {
    return parseTemplateListResponse((data as { data: unknown }).data)
  }
  return []
}
